import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { isMarketingDomain } from '@personic/shared'
import { AuthGuard, StatusPageGuard, DashboardRedirect } from '@/guards/AuthGuard'

// Layouts
import DashboardLayout from '@/components/layout/DashboardLayout'
import OnboardingLayout from '@/components/layout/OnboardingLayout'

// Employee pages
import EmployeeDashboard from '@/pages/employee/Dashboard'
import Tenants from '@/pages/employee/Tenants'
import TenantDetailPage from '@/pages/employee/TenantDetailPage'
import AdminPanel from '@/pages/employee/AdminPanel'
import EmailManagement from '@/pages/employee/EmailManagement'
import Features from '@/pages/employee/Features'
import SystemConfig from '@/pages/employee/SystemConfig'
import Support from '@/pages/employee/Support'
import DevTools from '@/pages/employee/DevTools'
import ApiExplorer from '@/pages/employee/ApiExplorer'
import LogViewer from '@/pages/employee/LogViewer'
import Health from '@/pages/employee/Health'
import MindMetaLAdmin from '@/pages/employee/mindmetal'
import ProfileBrowser from '@/pages/employee/mindmetal/ProfileBrowser'
import ProfileDetail from '@/pages/employee/mindmetal/ProfileDetail'
import PragmaticTester from '@/pages/employee/mindmetal/PragmaticTester'
import { AttestationQueue, AttestationReview } from '@/pages/employee/attestations'
import ConsumerAdmin from '@/pages/employee/consumer/ConsumerAdmin'
import UserDirectory from '@/pages/employee/admin/UserDirectory'
import UserDetail from '@/pages/employee/admin/UserDetail'
import ExtensionDownload from '@/pages/ExtensionDownload'
import MyProfileViewer from '@/pages/admin/MyProfileViewer'

// Memory pages
import MemoryManagement from '@/pages/tenant/memory'
import MemoryAdmin from '@/pages/employee/memory'

// Tenant pages (existing — preserved for backward compat)
import TenantDashboard from '@/pages/tenant/Dashboard'
import Profiles from '@/pages/tenant/Profiles'
import ProfileViewer from '@/pages/tenant/ProfileViewer'
import PendingApproval from '@/pages/tenant/PendingApproval'
import Suspended from '@/pages/tenant/Suspended'
import Attestations from '@/pages/tenant/attestations'
import AttestationDetail from '@/pages/tenant/attestations/Detail'

// Persona pages
import PersonasDashboard from '@/pages/tenant/personas'
import PersonasBrowse from '@/pages/tenant/personas/Browse'
import PersonasConfiguration from '@/pages/tenant/personas/Configuration'
import PersonaDetail from '@/pages/tenant/personas/Detail'

// Engage pages
import EngageDashboardLegacy from '@/pages/tenant/engage'
import EngageMonitor from '@/pages/tenant/engage/Monitor'
import EngagePersonas from '@/pages/tenant/engage/Personas'
import EngageRelationships from '@/pages/tenant/engage/Relationships'
import EngageSettings from '@/pages/tenant/engage/Settings'
import PragmaticSettings from '@/pages/tenant/engage/PragmaticSettings'
import CoachLens from '@/pages/tenant/engage/CoachLens'
import PersonaEngineHealth from '@/pages/tenant/engage/PersonaEngineHealth'
import PersonaEngineInstances from '@/pages/tenant/engage/PersonaEngineInstances'

// Persona Engine Admin pages
import PersonaEngineAdminDashboard from '@/pages/admin/persona-engine/PersonaEngineAdminDashboard'
import ArchetypeLibrary from '@/pages/admin/persona-engine/ArchetypeLibrary'
import RoleTemplateManager from '@/pages/admin/persona-engine/RoleTemplateManager'
import GraduationDashboard from '@/pages/admin/persona-engine/GraduationDashboard'
import PersonaEngineConfig from '@/pages/admin/persona-engine/PersonaEngineConfig'

// === NEW: API & Integrations pages ===
import SettingsApiKeys from '@/pages/settings/integrations/ApiKeys'
import SettingsWebhooks from '@/pages/settings/integrations/Webhooks'
import SettingsChannels from '@/pages/settings/integrations/Channels'
import SettingsThirdParty from '@/pages/settings/integrations/ThirdParty'
import SettingsDeveloper from '@/pages/settings/integrations/Developer'

// === NEW: Compliance pages ===
import SettingsComplianceStatus from '@/pages/settings/compliance/Status'
import SettingsComplianceAudit from '@/pages/settings/compliance/Audit'
import SettingsComplianceReports from '@/pages/settings/compliance/Reports'
import SettingsComplianceResidency from '@/pages/settings/compliance/Residency'

// === NEW: Calibration pages ===
import SettingsCalibrationOverview from '@/pages/settings/calibration/Overview'
import SettingsCalibrationDimensions from '@/pages/settings/calibration/Dimensions'
import SettingsCalibrationPresets from '@/pages/settings/calibration/Presets'
import SettingsCalibrationTest from '@/pages/settings/calibration/Test'
import SettingsCalibrationHistory from '@/pages/settings/calibration/History'

// === NEW: Governance pages ===
import SettingsRetention from '@/pages/settings/governance/Retention'
import SettingsStorage from '@/pages/settings/governance/Storage'
import SettingsDeletion from '@/pages/settings/governance/Deletion'
import SettingsGovernanceDashboard from '@/pages/settings/governance/Dashboard'

// === NEW: Notifications pages ===
import SettingsNotificationPreferences from '@/pages/settings/notifications/Preferences'
import SettingsNotificationAlerts from '@/pages/settings/notifications/Alerts'

// === NEW: Home, Engage, Admin entry points ===
import Home from '@/pages/Home'
import EngageDashboard from '@/pages/engage/EngageDashboard'
import AdminTenantBrowser from '@/pages/admin/tenants/Browser'
import { ScaffoldPage } from '@/components/layout/ScaffoldPage'
import { buildBreadcrumbs } from '@/config/routes'

// === NEW: Platform Settings pages ===
import SettingsOrganization from '@/pages/settings/account/Organization'
import SettingsSubscription from '@/pages/settings/account/Subscription'
import SettingsBilling from '@/pages/settings/account/Billing'
import SettingsUsage from '@/pages/settings/account/Usage'
import SettingsMembers from '@/pages/settings/team/Members'
import SettingsRoles from '@/pages/settings/team/Roles'
import SettingsTeamAudit from '@/pages/settings/team/Audit'
import SettingsSso from '@/pages/settings/team/Sso'

// Onboarding pages
import {
  Welcome,
  ProfileSetup,
  OrganizationCreation,
  ProductSelection,
  AttestationOverview,
  FrameworkSelection,
  AttestationForm,
  AttestationConfirmation,
  InviteTeam,
  SetupComplete,
} from '@/pages/onboarding'

// Engage scaffold pages (future features)
function EngageSkillsScaffold() {
  return <ScaffoldPage title="Skill Suite" description="Build and manage AI conversation skills." breadcrumbs={buildBreadcrumbs('/engage/skills')} plannedFeatures={['Pre-built skill templates', 'Custom skill creation', 'Skill performance analytics', 'A/B testing framework']} />
}
function EngageChannelsScaffold() {
  return <ScaffoldPage title="Channels & Deployment" description="Deploy personas across communication channels." breadcrumbs={buildBreadcrumbs('/engage/channels')} plannedFeatures={['Multi-channel deployment', 'Channel-specific persona tuning', 'Deployment monitoring', 'Channel analytics']} />
}
function EngageIntelligenceScaffold() {
  return <ScaffoldPage title="Customer Intelligence" description="Deep insights from customer interactions." breadcrumbs={buildBreadcrumbs('/engage/intelligence')} plannedFeatures={['Sentiment trends', 'Topic clustering', 'Customer journey mapping', 'Interaction heatmaps']} />
}
function EngageAnalyticsScaffold() {
  return <ScaffoldPage title="Analytics & Reports" description="Comprehensive reporting for the Engage platform." breadcrumbs={buildBreadcrumbs('/engage/analytics')} plannedFeatures={['Real-time dashboards', 'Custom report builder', 'Scheduled exports', 'Performance benchmarks']} />
}

// Redirect marketing/auth paths that land on portal domain back to marketing
function MarketingRedirect() {
  const path = window.location.pathname
  window.location.href = `https://personicai.com${path}`
  return null
}

function App() {
  // If somehow we're on the marketing domain, redirect everything to marketing site
  if (isMarketingDomain()) {
    window.location.href = `https://personicai.com${window.location.pathname}`
    return null
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Redirect auth paths to marketing domain */}
        <Route path="/auth/*" element={<MarketingRedirect />} />

        {/* Backwards compatibility redirects */}
        <Route path="/login" element={<MarketingRedirect />} />
        <Route path="/signup" element={<MarketingRedirect />} />

        {/* Onboarding routes */}
        <Route
          path="/onboarding"
          element={
            <AuthGuard allowedRoles={['tenant_admin']} requireActiveTenant={false}>
              <OnboardingLayout />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/onboarding/welcome" replace />} />
          <Route path="welcome" element={<Welcome />} />
          <Route path="profile" element={<ProfileSetup />} />
          <Route path="organization" element={<OrganizationCreation />} />
          <Route path="products" element={<ProductSelection />} />
          <Route path="attestation" element={<AttestationOverview />} />
          <Route path="attestation/frameworks" element={<FrameworkSelection />} />
          <Route path="attestation/form/:framework" element={<AttestationForm />} />
          <Route path="attestation/confirmation" element={<AttestationConfirmation />} />
          <Route path="invite-team" element={<InviteTeam />} />
          <Route path="complete" element={<SetupComplete />} />
        </Route>

        {/* ===== Main authenticated routes (DashboardLayout) ===== */}
        <Route
          element={
            <AuthGuard>
              <DashboardLayout />
            </AuthGuard>
          }
        >
          {/* Home Dashboard */}
          <Route path="/" element={<Home />} />

          {/* Extension download */}
          <Route path="/extension" element={<ExtensionDownload />} />

          {/* === NEW: Platform Settings === */}
          <Route path="/settings/account" element={<Navigate to="/settings/account/organization" replace />} />
          <Route path="/settings/account/organization" element={<SettingsOrganization />} />
          <Route path="/settings/account/subscription" element={<SettingsSubscription />} />
          <Route path="/settings/account/billing" element={<SettingsBilling />} />
          <Route path="/settings/account/usage" element={<SettingsUsage />} />
          <Route path="/settings/team" element={<Navigate to="/settings/team/members" replace />} />
          <Route path="/settings/team/members" element={<SettingsMembers />} />
          <Route path="/settings/team/roles" element={<SettingsRoles />} />
          <Route path="/settings/team/audit" element={<SettingsTeamAudit />} />
          <Route path="/settings/team/sso" element={<SettingsSso />} />

          {/* === NEW: API & Integrations === */}
          <Route path="/settings/integrations" element={<Navigate to="/settings/integrations/api-keys" replace />} />
          <Route path="/settings/integrations/api-keys" element={<SettingsApiKeys />} />
          <Route path="/settings/integrations/webhooks" element={<SettingsWebhooks />} />
          <Route path="/settings/integrations/channels" element={<SettingsChannels />} />
          <Route path="/settings/integrations/third-party" element={<SettingsThirdParty />} />
          <Route path="/settings/integrations/developer" element={<SettingsDeveloper />} />

          {/* === NEW: Compliance === */}
          <Route path="/settings/compliance" element={<Navigate to="/settings/compliance/status" replace />} />
          <Route path="/settings/compliance/status" element={<SettingsComplianceStatus />} />
          <Route path="/settings/compliance/audit" element={<SettingsComplianceAudit />} />
          <Route path="/settings/compliance/reports" element={<SettingsComplianceReports />} />
          <Route path="/settings/compliance/residency" element={<SettingsComplianceResidency />} />

          {/* === NEW: Calibration === */}
          <Route path="/settings/calibration" element={<Navigate to="/settings/calibration/overview" replace />} />
          <Route path="/settings/calibration/overview" element={<SettingsCalibrationOverview />} />
          <Route path="/settings/calibration/dimensions" element={<SettingsCalibrationDimensions />} />
          <Route path="/settings/calibration/presets" element={<SettingsCalibrationPresets />} />
          <Route path="/settings/calibration/test" element={<SettingsCalibrationTest />} />
          <Route path="/settings/calibration/history" element={<SettingsCalibrationHistory />} />

          {/* === NEW: Governance === */}
          <Route path="/settings/governance" element={<Navigate to="/settings/governance/retention" replace />} />
          <Route path="/settings/governance/retention" element={<SettingsRetention />} />
          <Route path="/settings/governance/storage" element={<SettingsStorage />} />
          <Route path="/settings/governance/deletion" element={<SettingsDeletion />} />
          <Route path="/settings/governance/dashboard" element={<SettingsGovernanceDashboard />} />

          {/* === NEW: Notifications === */}
          <Route path="/settings/notifications" element={<Navigate to="/settings/notifications/preferences" replace />} />
          <Route path="/settings/notifications/preferences" element={<SettingsNotificationPreferences />} />
          <Route path="/settings/notifications/alerts" element={<SettingsNotificationAlerts />} />

          {/* === NEW: Engage entry point === */}
          <Route path="/engage" element={<Navigate to="/engage/dashboard" replace />} />
          <Route path="/engage/dashboard" element={<EngageDashboard />} />
          <Route path="/engage/personas" element={<Navigate to="/tenant/engage/personas" replace />} />
          <Route path="/engage/skills" element={<EngageSkillsScaffold />} />
          <Route path="/engage/channels" element={<EngageChannelsScaffold />} />
          <Route path="/engage/intelligence" element={<EngageIntelligenceScaffold />} />
          <Route path="/engage/analytics" element={<EngageAnalyticsScaffold />} />

          {/* === NEW: Admin entry point === */}
          <Route path="/admin" element={<Navigate to="/admin/tenants" replace />} />
          <Route path="/admin/my-profile" element={<MyProfileViewer />} />
          <Route path="/admin/tenants" element={<AdminTenantBrowser />} />
          <Route path="/admin/users" element={<UserDirectory />} />
          <Route path="/admin/users/:id" element={<UserDetail />} />
          <Route path="/admin/testing" element={<Navigate to="/employee/dev-tools" replace />} />
          <Route path="/admin/models" element={<Navigate to="/employee/mindmetal" replace />} />
          <Route path="/admin/analytics" element={<Navigate to="/employee/dashboard" replace />} />
          <Route path="/admin/compliance" element={<Navigate to="/employee/attestations" replace />} />
          <Route path="/admin/ops" element={<Navigate to="/employee/health" replace />} />
          <Route path="/admin/config" element={<Navigate to="/employee/system" replace />} />
          <Route path="/admin/internal" element={<Navigate to="/employee/dev-tools" replace />} />

          {/* === Persona Engine Admin === */}
          <Route path="/admin/persona-engine" element={<PersonaEngineAdminDashboard />} />
          <Route path="/admin/persona-engine/archetypes" element={<ArchetypeLibrary />} />
          <Route path="/admin/persona-engine/roles" element={<RoleTemplateManager />} />
          <Route path="/admin/persona-engine/graduation" element={<GraduationDashboard />} />
          <Route path="/admin/persona-engine/config" element={<PersonaEngineConfig />} />

          {/* === LEGACY: Employee routes === */}
          <Route path="/employee" element={<Navigate to="/employee/dashboard" replace />} />
          <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
          <Route path="/employee/tenants" element={<Tenants />} />
          <Route path="/employee/tenants/:id" element={<TenantDetailPage />} />
          <Route path="/employee/admin" element={<AdminPanel />} />
          <Route path="/employee/emails" element={<EmailManagement />} />
          <Route path="/employee/features" element={<Features />} />
          <Route path="/employee/attestations" element={<AttestationQueue />} />
          <Route path="/employee/attestations/:id" element={<AttestationReview />} />
          <Route path="/employee/mindmetal" element={<MindMetaLAdmin />} />
          <Route path="/employee/mindmetal/profiles" element={<ProfileBrowser />} />
          <Route path="/employee/mindmetal/profiles/:id" element={<ProfileDetail />} />
          <Route path="/employee/mindmetal/pragmatic" element={<PragmaticTester />} />
          <Route path="/employee/system" element={<SystemConfig />} />
          <Route path="/employee/support" element={<Support />} />
          <Route path="/employee/dev-tools" element={<DevTools />} />
          <Route path="/employee/dev-tools/api" element={<ApiExplorer />} />
          <Route path="/employee/dev-tools/logs" element={<LogViewer />} />
          <Route path="/employee/health" element={<Health />} />
          <Route path="/employee/consumer" element={<ConsumerAdmin />} />
          <Route path="/employee/admin/users" element={<Navigate to="/admin/users" replace />} />
          <Route path="/employee/admin/users/:id" element={<Navigate to="/admin/users/:id" replace />} />
          <Route path="/employee/memory" element={<MemoryAdmin />} />

          {/* === LEGACY: Tenant routes === */}
          <Route path="/tenant/dashboard" element={<TenantDashboard />} />
          <Route path="/tenant/profiles" element={<Profiles />} />
          <Route path="/tenant/profiles/:id" element={<ProfileViewer />} />
          <Route path="/tenant/attestations" element={<Attestations />} />
          <Route path="/tenant/attestations/:id" element={<AttestationDetail />} />
          <Route path="/tenant/personas" element={<PersonasDashboard />} />
          <Route path="/tenant/personas/browse" element={<PersonasBrowse />} />
          <Route path="/tenant/personas/configuration" element={<PersonasConfiguration />} />
          <Route path="/tenant/personas/:id" element={<PersonaDetail />} />
          <Route path="/tenant/engage" element={<EngageDashboardLegacy />} />
          <Route path="/tenant/engage/monitor" element={<EngageMonitor />} />
          <Route path="/tenant/engage/personas" element={<EngagePersonas />} />
          <Route path="/tenant/engage/relationships" element={<EngageRelationships />} />
          <Route path="/tenant/engage/settings" element={<EngageSettings />} />
          <Route path="/tenant/engage/pragmatic" element={<PragmaticSettings />} />
          <Route path="/tenant/engage/coach" element={<CoachLens />} />
          <Route path="/tenant/engage/persona-engine-health" element={<PersonaEngineHealth />} />
          <Route path="/tenant/engage/persona-engine-instances" element={<PersonaEngineInstances />} />

          {/* === Memory Module === */}
          <Route path="/tenant/memory" element={<MemoryManagement />} />
          <Route path="/tenant/memory/config" element={<MemoryManagement />} />
          <Route path="/tenant/memory/sovereignty" element={<MemoryManagement />} />

          {/* === Phase 1 Redirects: old tenant routes → new settings routes === */}
          <Route path="/tenant/settings" element={<Navigate to="/settings/account/organization" replace />} />
          <Route path="/tenant/team" element={<Navigate to="/settings/team/members" replace />} />
          <Route path="/tenant/api-keys" element={<Navigate to="/settings/integrations/api-keys" replace />} />
          <Route path="/tenant/billing" element={<Navigate to="/settings/account/subscription" replace />} />
          <Route path="/tenant/billing/plan" element={<Navigate to="/settings/account/subscription" replace />} />
          <Route path="/tenant/billing/features" element={<Navigate to="/settings/account/subscription" replace />} />
          <Route path="/tenant/billing/history" element={<Navigate to="/settings/account/billing" replace />} />
          <Route path="/tenant/calibration" element={<Navigate to="/settings/calibration/overview" replace />} />
          <Route path="/tenant/calibration/dimensions" element={<Navigate to="/settings/calibration/dimensions" replace />} />
          <Route path="/tenant/calibration/presets" element={<Navigate to="/settings/calibration/presets" replace />} />
          <Route path="/tenant/calibration/testing" element={<Navigate to="/settings/calibration/test" replace />} />
          <Route path="/tenant/developers" element={<Navigate to="/settings/integrations/developer" replace />} />
          <Route path="/tenant/developers/playground" element={<Navigate to="/settings/integrations/developer" replace />} />
          <Route path="/tenant/developers/webhooks" element={<Navigate to="/settings/integrations/webhooks" replace />} />
          <Route path="/tenant/developers/sdk" element={<Navigate to="/settings/integrations/developer" replace />} />
          <Route path="/tenant/developers/logs" element={<Navigate to="/settings/integrations/developer" replace />} />
          <Route path="/tenant/distillation" element={<Navigate to="/settings/calibration/overview" replace />} />
          <Route path="/tenant/distillation/features" element={<Navigate to="/settings/calibration/dimensions" replace />} />
          <Route path="/tenant/distillation/advanced" element={<Navigate to="/settings/calibration/overview" replace />} />
        </Route>

        {/* Tenant status pages (outside DashboardLayout) */}
        <Route
          path="/tenant/pending"
          element={
            <StatusPageGuard>
              <PendingApproval />
            </StatusPageGuard>
          }
        />
        <Route
          path="/tenant/suspended"
          element={
            <StatusPageGuard>
              <Suspended />
            </StatusPageGuard>
          }
        />

        {/* Redirect authenticated users to their dashboard */}
        <Route path="/dashboard" element={<DashboardRedirect />} />

        {/* 404 - redirect to dashboard */}
        <Route path="*" element={<DashboardRedirect />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
